import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { OnboardingState, UserInitializationError, UserInitializationResponse } from '../types'
import { createUserInitializationError } from '../types'
import { env } from '@/infra/config/env'
import { API_ENDPOINTS, httpPost } from '@/infra/api'
import { useAuthStatus } from '../hooks'

interface UserInitializationState {
  readonly userInitialized: boolean
  readonly initializingUser: boolean
  readonly initializationError: UserInitializationError | null
  readonly initializationAttempts: number
}

interface UserInitializationProviderProps {
  readonly children: ReactNode
}

export interface UserInitializationContextValue extends UserInitializationState {
  readonly retryInitialization: () => void
  readonly onboardingState: OnboardingState | null
  readonly updateOnboardingStep: (step: keyof OnboardingState) => void
}

const UserInitializationContext = createContext<UserInitializationContextValue | null>(null)

export const UserInitializationProvider: FC<UserInitializationProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStatus()
  const accessToken = (user as { access_token?: string } | null)?.access_token

  const [state, setState] = useState<UserInitializationState>({
    userInitialized: false,
    initializingUser: false,
    initializationError: null,
    initializationAttempts: 0,
  })

  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null)

  const initializeUserRef = useRef<(() => Promise<void>) | null>(null)
  const isInitializingRef = useRef(false)
  const hasInitializedForTokenRef = useRef<string | null>(null)

  const scheduleRetryAttempt = useCallback((): void => {
    setTimeout(() => {
      if (initializeUserRef.current) {
        initializeUserRef.current().catch(() => {})
      }
    }, env.USER_INIT_RETRY_DELAY_MS)
  }, [])

  const handleInitializationError = useCallback(
    (error: UserInitializationError): void => {
      setState((prev) => {
        if (prev.initializationAttempts < env.USER_INIT_MAX_ATTEMPTS) {
          scheduleRetryAttempt()
        }

        return {
          ...prev,
          initializingUser: false,
          initializationError:
            prev.initializationAttempts >= env.USER_INIT_MAX_ATTEMPTS
              ? createUserInitializationError.maxAttemptsReached(prev.initializationAttempts)
              : error,
        }
      })
      isInitializingRef.current = false
    },
    [scheduleRetryAttempt],
  )

  const initializeUser = useCallback(async (): Promise<void> => {
    if (!accessToken) {
      setState((prev) => ({
        ...prev,
        initializationError: createUserInitializationError.noAccessToken(),
        initializingUser: false,
      }))
      return
    }

    if (hasInitializedForTokenRef.current === accessToken || isInitializingRef.current) {
      return
    }

    isInitializingRef.current = true

    setState((prev) => {
      if (prev.initializationAttempts >= env.USER_INIT_MAX_ATTEMPTS) {
        isInitializingRef.current = false
        return {
          ...prev,
          initializationError: createUserInitializationError.maxAttemptsReached(
            env.USER_INIT_MAX_ATTEMPTS,
          ),
          initializingUser: false,
        }
      }

      return {
        ...prev,
        initializingUser: true,
        initializationError: null,
        initializationAttempts: prev.initializationAttempts + 1,
      }
    })

    try {
      const result = await httpPost<UserInitializationResponse>(API_ENDPOINTS.USER_INITIALIZE)

      if (result.success) {
        hasInitializedForTokenRef.current = accessToken
        setOnboardingState(result.data.onboarding)
        setState((prev) => ({
          ...prev,
          userInitialized: true,
          initializingUser: false,
          initializationError: null,
          initializationAttempts: 0,
        }))
        isInitializingRef.current = false
        return
      }

      if (result.error === 'network_error' || result.error === 'timeout') {
        handleInitializationError(createUserInitializationError.networkError(result.message))
      } else if (result.error === 'unauthorized' || result.error === 'forbidden') {
        handleInitializationError(
          createUserInitializationError.httpError(result.error === 'unauthorized' ? 401 : 403),
        )
      } else {
        handleInitializationError(createUserInitializationError.httpError(500))
      }
    } catch (error) {
      const initError =
        error instanceof Error
          ? createUserInitializationError.networkError(error.message, error.cause as string)
          : createUserInitializationError.networkError('User initialization failed')

      handleInitializationError(initError)
    }
  }, [accessToken, handleInitializationError])

  initializeUserRef.current = initializeUser

  const updateOnboardingStep = useCallback((step: keyof OnboardingState): void => {
    setOnboardingState((prev) => {
      if (!prev) return prev
      return { ...prev, [step]: true }
    })
  }, [])

  const retryInitialization = useCallback((): void => {
    isInitializingRef.current = false
    hasInitializedForTokenRef.current = null
    setOnboardingState(null)
    setState((prev) => ({
      ...prev,
      initializationError: null,
      initializationAttempts: 0,
    }))
    initializeUser().catch(() => {})
  }, [initializeUser])

  useEffect(() => {
    if (isAuthenticated && accessToken && !state.userInitialized) {
      initializeUser().catch(() => {})
    }
  }, [isAuthenticated, accessToken])

  const contextValue = useMemo<UserInitializationContextValue>(
    () => ({
      userInitialized: state.userInitialized,
      initializingUser: state.initializingUser,
      initializationError: state.initializationError,
      initializationAttempts: state.initializationAttempts,
      retryInitialization,
      onboardingState,
      updateOnboardingStep,
    }),
    [
      state.userInitialized,
      state.initializingUser,
      state.initializationError,
      state.initializationAttempts,
      retryInitialization,
      onboardingState,
      updateOnboardingStep,
    ],
  )

  return (
    <UserInitializationContext.Provider value={contextValue}>
      {children}
    </UserInitializationContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserInitializationContext(): UserInitializationContextValue {
  const context = useContext(UserInitializationContext)
  if (!context) {
    throw new Error('useUserInitializationContext must be used within UserInitializationProvider')
  }
  return context
}
